/**
 * Utility functions for formatting numbers and currency
 */

/**
 * Format a number with thousand separators
 * @param {number} num - The number to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number string
 */
export const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined) return '0';
  
  const number = typeof num === 'string' ? parseFloat(num) : num;
  
  if (isNaN(number)) return '0';
  
  return number.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Format a number as currency (USD)
 * @param {number} amount - The amount to format
 * @param {boolean} showCents - Whether to show cents (default: true)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, showCents = true) => {
  if (amount === null || amount === undefined) return '$0.00';
  
  const number = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(number)) return '$0.00';
  
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0
  });
};

/**
 * Format a percentage
 * @param {number} value - The percentage value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined) return '0%';
  
  const number = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(number)) return '0%';
  
  return `${number.toFixed(decimals)}%`;
};

/**
 * Format large numbers with abbreviations (K, M, B)
 * @param {number} num - The number to format
 * @returns {string} Formatted abbreviated number
 */
export const formatAbbreviated = (num) => {
  if (num === null || num === undefined) return '0';
  
  const number = typeof num === 'string' ? parseFloat(num) : num;
  
  if (isNaN(number)) return '0';
  
  if (Math.abs(number) >= 1e9) {
    return (number / 1e9).toFixed(1) + 'B';
  } else if (Math.abs(number) >= 1e6) {
    return (number / 1e6).toFixed(1) + 'M';
  } else if (Math.abs(number) >= 1e3) {
    return (number / 1e3).toFixed(1) + 'K';
  }
  
  return number.toFixed(0);
};